import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { designTemplates, applyTheme, generateCSSVariables, resolveThemeId } from '../lib/themes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Default branding settings - will be overwritten by API
const defaultBranding = {
  templateId: null, // No default - will be loaded from API
  platformName: '',
  logoUrl: null,
  logoSize: 40,
  faviconUrl: null,
  showPlatformName: true,
  backgroundImage: null,
  customColors: {},
  borderRadius: '8px',
  fontHeading: "'Playfair Display', serif",
  fontBody: "'Manrope', sans-serif",
  defaultLanguage: 'fr',
  legalNotice: '',
  privacyPolicy: '',
  termsOfService: '',
  // Video settings
  welcomeVideoEnabled: false,
  welcomeVideoUrl: '',
  welcomeVideoEmbed: '',
  welcomeVideoFile: null,
  welcomeVideoTitle: '',
  welcomeVideoPosition: 'top',
  welcomeVideoAutoplay: false,
  welcomeVideoHeight: 'medium',
  welcomeVideoLoop: false,
  welcomeVideoCropTop: 0,
  welcomeVideoCropBottom: 0,
  welcomeVideoObjectPosition: '50% 50%',
  // Client dashboard background
  clientBackgroundEnabled: false,
  clientBackgroundUrl: '',
  clientBackgroundFile: null,
  clientBackgroundOpacity: 0.3,
  // PWA icon
  pwaIcon: null,
};

// Cache key for localStorage
const BRANDING_CACHE_KEY = 'pb_branding_cache';

// Get cached branding from localStorage
const getCachedBranding = () => {
  try {
    const cached = localStorage.getItem(BRANDING_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.warn('Failed to read branding cache:', e);
  }
  return null;
};

// Save branding to localStorage cache
const setCachedBranding = (branding) => {
  try {
    // Create a cache-safe version without large base64 data
    const cacheSafe = { ...branding };
    
    // If logoUrl is base64, keep only a small version or skip it for cache
    // Base64 images can be very large and exceed localStorage quota
    if (cacheSafe.logoUrl && cacheSafe.logoUrl.startsWith('data:')) {
      // Keep the base64 logo but localStorage might reject it
      // We'll catch the error and retry without it
    }
    
    // Remove potentially large base64 fields to avoid quota errors
    const largeFields = ['welcomeVideoFile', 'clientBackgroundFile'];
    largeFields.forEach(field => {
      if (cacheSafe[field] && typeof cacheSafe[field] === 'string' && cacheSafe[field].startsWith('data:') && cacheSafe[field].length > 50000) {
        cacheSafe[field] = null;
      }
    });
    
    try {
      localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(cacheSafe));
    } catch (quotaError) {
      // If quota exceeded, try without logoUrl if it's base64
      if (cacheSafe.logoUrl && cacheSafe.logoUrl.startsWith('data:')) {
        console.warn('Logo too large for cache, storing without logo');
        const withoutLogo = { ...cacheSafe, logoUrl: null };
        localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(withoutLogo));
      } else {
        throw quotaError;
      }
    }
  } catch (e) {
    console.warn('Failed to cache branding:', e);
  }
};

export const ThemeProvider = ({ children }) => {
  // Initialize with cached branding if available
  const cachedBranding = getCachedBranding();
  const initialBranding = cachedBranding || defaultBranding;
  
  const [branding, setBranding] = useState(initialBranding);
  // Only use template if we have a valid templateId from cache
  const initialTemplateId = initialBranding.templateId;
  const [currentTemplate, setCurrentTemplate] = useState(
    initialTemplateId && designTemplates[initialTemplateId] ? designTemplates[initialTemplateId] : null
  );
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(
    initialTemplateId && designTemplates[initialTemplateId] ? designTemplates[initialTemplateId].style === 'dark' : true
  );

  // Apply cached theme immediately on mount - ONLY if we have valid cache
  useEffect(() => {
    if (cachedBranding && cachedBranding.templateId && designTemplates[resolveThemeId(cachedBranding.templateId)]) {
      const tid = resolveThemeId(cachedBranding.templateId);
      applyTheme(tid, cachedBranding.customColors, {
        backgroundImage: cachedBranding.backgroundImage,
        borderRadius: cachedBranding.borderRadius,
        fontHeading: cachedBranding.fontHeading,
        fontBody: cachedBranding.fontBody,
      });
      if (cachedBranding.faviconUrl && sessionStorage.getItem('pin_verified') === 'true' && window.location.pathname !== '/' && window.location.pathname !== '') {
        updateFavicon(cachedBranding.faviconUrl);
      }
      if (sessionStorage.getItem('pin_verified') === 'true' && window.location.pathname !== '/' && window.location.pathname !== '') {
        document.title = cachedBranding.platformName || '';
      }
      // Show body since we have a valid cached theme
      document.body.classList.add('theme-ready');
    }
    // If no valid cache, body stays hidden until fetchBranding completes
  }, []); // Run once on mount

  // Update favicon
  const updateFavicon = (url) => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  };

  const updatePwaIcon = (url) => {
    if (!url) return;
    const fullUrl = url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
    const link = document.getElementById('apple-touch-icon');
    if (link) link.href = fullUrl;
  };

  // Fetch branding settings from backend
  const fetchBranding = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/platform`);
      const data = response.data;
      
      // IMPORTANT: template_id MUST come from the database, no fallback
      if (!data.template_id) {
        console.error('No template_id in platform settings!');
      }
      
      // Map backend settings to frontend branding
      const newBranding = {
        templateId: resolveThemeId(data.template_id), // Map legacy theme IDs
        platformName: data.platform_name || '',
        logoUrl: data.logo_url || null,
        logoSize: data.logo_size || 40,
        faviconUrl: data.favicon_url || null,
        showPlatformName: data.show_platform_name !== false,
        backgroundImage: data.background_image || branding?.backgroundImage || null,
        customColors: data.custom_colors || {},
        borderRadius: data.border_radius || '8px',
        fontHeading: data.font_heading || "'Playfair Display', serif",
        fontBody: data.font_body || "'Manrope', sans-serif",
        defaultLanguage: data.default_language || 'fr',
        legalNotice: data.legal_notice || '',
        privacyPolicy: data.privacy_policy || '',
        termsOfService: data.terms_of_service || '',
        // Video settings
        welcomeVideoEnabled: data.welcome_video_enabled || false,
        welcomeVideoUrl: data.welcome_video_url || '',
        welcomeVideoEmbed: data.welcome_video_embed || '',
        welcomeVideoFile: data.welcome_video_file || branding?.welcomeVideoFile || null,
        welcomeVideoTitle: data.welcome_video_title || '',
        welcomeVideoPosition: data.welcome_video_position || 'top',
        welcomeVideoAutoplay: data.welcome_video_autoplay || false,
        welcomeVideoLoop: data.welcome_video_loop || false,
        welcomeVideoHeight: data.welcome_video_height || 'medium',
        welcomeVideoCropTop: data.welcome_video_crop_top || 0,
        welcomeVideoCropBottom: data.welcome_video_crop_bottom || 0,
        welcomeVideoObjectPosition: data.welcome_video_object_position || '50% 50%',
        // Client dashboard background
        clientBackgroundEnabled: data.client_background_enabled || false,
        clientBackgroundUrl: data.client_background_url || '',
        clientBackgroundFile: data.client_background_file || null,
        clientBackgroundOpacity: data.client_background_opacity || 0.3,
        pwaIcon: data.pwa_icon || null,
        // OG / Sharing meta
        ogTitle: data.og_title || '',
        ogDescription: data.og_description || '',
        ogImage: data.og_image || '',
      };
      
      // Save to cache for instant loading on refresh
      setCachedBranding(newBranding);
      
      setBranding(newBranding);
      
      // Apply theme - ONLY use the template from the database
      const template = designTemplates[newBranding.templateId];
      if (template) {
        setCurrentTemplate(template);
        setIsDark(template.style === 'dark');
        
        applyTheme(newBranding.templateId, newBranding.customColors, {
          backgroundImage: newBranding.backgroundImage,
          borderRadius: newBranding.borderRadius,
          fontHeading: newBranding.fontHeading,
          fontBody: newBranding.fontBody,
        });
      } else {
        console.error('Template not found:', newBranding.templateId);
      }
      
      // ALL branding updates (favicon, title, OG) only after PIN verified AND not on PIN page
      const pinOk = sessionStorage.getItem('pin_verified') === 'true';
      const isRootPage = window.location.pathname === '/' || window.location.pathname === '';
      if (pinOk && !isRootPage) {
        if (newBranding.faviconUrl) updateFavicon(newBranding.faviconUrl);
        if (newBranding.pwaIcon) updatePwaIcon(newBranding.pwaIcon);
        document.title = newBranding.platformName;
        const mOgT = document.querySelector('meta[property="og:title"]');
        const mOgD = document.querySelector('meta[property="og:description"]');
        const mD = document.querySelector('meta[name="description"]');
        const mOgI = document.querySelector('meta[property="og:image"]');
        if (mOgT) mOgT.setAttribute('content', newBranding.ogTitle || newBranding.platformName || '');
        if (mOgD) mOgD.setAttribute('content', newBranding.ogDescription || '');
        if (mD) mD.setAttribute('content', newBranding.ogDescription || '');
        if (mOgI) mOgI.setAttribute('content', newBranding.ogImage || '');
      } else if (pinOk && isRootPage) {
        // Store for later - will be applied when route changes
        window.__pendingBranding = newBranding;
      }
      
      // Hide splash screen and show app
      if (typeof window.__hideSplash === 'function') {
        window.__hideSplash();
      }
      
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      // On error, still hide splash and show app
      if (typeof window.__hideSplash === 'function') {
        window.__hideSplash();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Apply pending branding when leaving PIN page
  useEffect(() => {
    const applyPending = () => {
      const b = window.__pendingBranding;
      if (b && window.location.pathname !== '/' && window.location.pathname !== '') {
        document.title = b.platformName || '';
        if (b.faviconUrl) updateFavicon(b.faviconUrl);
        const mOgT = document.querySelector('meta[property="og:title"]');
        if (mOgT) mOgT.setAttribute('content', b.ogTitle || b.platformName || '');
        window.__pendingBranding = null;
      }
    };
    // Check on interval in case route changed via React router (no page reload)
    const interval = setInterval(applyPending, 500);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, []);

  const [mediaLoaded, setMediaLoaded] = useState(false);

  // Lazy load heavy media only when needed (called by components that need it)
  const loadMedia = useCallback(async () => {
    if (mediaLoaded) return;
    try {
      const res = await axios.get(`${API}/settings/platform/media`);
      if (res.data) {
        setBranding(prev => ({
          ...prev,
          backgroundImage: res.data.background_image || prev?.backgroundImage || null,
          welcomeVideoFile: res.data.welcome_video_file || prev?.welcomeVideoFile || null,
        }));
        setMediaLoaded(true);
      }
    } catch {}
  }, [mediaLoaded]);

  // Force reload media (for admin after upload)
  const reloadMedia = useCallback(async () => {
    setMediaLoaded(false);
    try {
      const res = await axios.get(`${API}/settings/platform/media`);
      if (res.data) {
        setBranding(prev => ({
          ...prev,
          backgroundImage: res.data.background_image || null,
          welcomeVideoFile: res.data.welcome_video_file || null,
        }));
        setMediaLoaded(true);
      }
    } catch {}
  }, []);

  // Upload a media file (video/image) via multipart and get a URL back
  const uploadMediaFile = async (file, token, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/settings/upload-media`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data.url;
  };

  // Save branding settings
  const saveBranding = async (newBranding, token) => {
    try {
      // Map frontend branding to backend format
      const backendData = {
        template_id: newBranding.templateId,
        platform_name: newBranding.platformName,
        logo_url: newBranding.logoUrl,
        logo_size: newBranding.logoSize || 40,
        favicon_url: newBranding.faviconUrl,
        show_platform_name: newBranding.showPlatformName,
        background_image: newBranding.backgroundImage,
        custom_colors: newBranding.customColors,
        border_radius: newBranding.borderRadius,
        font_heading: newBranding.fontHeading,
        font_body: newBranding.fontBody,
        default_language: newBranding.defaultLanguage,
        legal_notice: newBranding.legalNotice,
        privacy_policy: newBranding.privacyPolicy,
        terms_of_service: newBranding.termsOfService,
        // Video settings (metadata only, not the file)
        welcome_video_enabled: newBranding.welcomeVideoEnabled,
        welcome_video_url: newBranding.welcomeVideoUrl,
        welcome_video_embed: newBranding.welcomeVideoEmbed,
        welcome_video_title: newBranding.welcomeVideoTitle,
        welcome_video_position: newBranding.welcomeVideoPosition,
        // Client dashboard background
        client_background_enabled: newBranding.clientBackgroundEnabled,
        client_background_url: newBranding.clientBackgroundUrl,
        client_background_opacity: newBranding.clientBackgroundOpacity,
        // PWA icon
        pwa_icon: newBranding.pwaIcon,
        // Video advanced settings
        welcome_video_autoplay: newBranding.welcomeVideoAutoplay,
        welcome_video_loop: newBranding.welcomeVideoLoop,
        welcome_video_height: newBranding.welcomeVideoHeight,
        welcome_video_crop_top: newBranding.welcomeVideoCropTop,
        welcome_video_crop_bottom: newBranding.welcomeVideoCropBottom,
        welcome_video_object_position: newBranding.welcomeVideoObjectPosition,
        // Legacy fields
        primary_color: newBranding.customColors.primary || currentTemplate.colors.primary,
        secondary_color: newBranding.customColors.background || currentTemplate.colors.background,
        accent_color: newBranding.customColors.accent || currentTemplate.colors.accent,
        dark_mode_default: isDark,
        // OG / Sharing meta
        og_title: newBranding.ogTitle || '',
        og_description: newBranding.ogDescription || '',
        og_image: newBranding.ogImage || '',
      };

      // If welcomeVideoFile is a URL (already uploaded), include it in settings
      if (newBranding.welcomeVideoFile && typeof newBranding.welcomeVideoFile === 'string' && newBranding.welcomeVideoFile.startsWith('/api/')) {
        backendData.welcome_video_file = newBranding.welcomeVideoFile;
      }
      
      await axios.put(`${API}/settings/platform`, backendData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save heavy files separately (background only - videos now use file upload)
      const heavyData = {};
      if (newBranding.clientBackgroundFile && newBranding.clientBackgroundFile.startsWith('data:')) {
        heavyData.client_background_file = newBranding.clientBackgroundFile;
      }
      if (Object.keys(heavyData).length > 0) {
        try {
          await axios.put(`${API}/settings/platform`, heavyData, {
            headers: { Authorization: `Bearer ${token}` },
            maxContentLength: 10 * 1024 * 1024,
            maxBodyLength: 10 * 1024 * 1024,
          });
        } catch (mediaErr) {
          console.warn('Media files may be too large:', mediaErr);
        }
      }

      setCachedBranding(newBranding);
      
      setBranding(newBranding);
      
      // Apply theme immediately - use ONLY the selected template
      const template = designTemplates[newBranding.templateId];
      if (template) {
        setCurrentTemplate(template);
        setIsDark(template.style === 'dark');
        
        applyTheme(newBranding.templateId, newBranding.customColors, {
          backgroundImage: newBranding.backgroundImage,
          borderRadius: newBranding.borderRadius,
          fontHeading: newBranding.fontHeading,
          fontBody: newBranding.fontBody,
        });
      }
      
      // Update favicon (only after PIN verified)
      if (newBranding.faviconUrl && sessionStorage.getItem('pin_verified') === 'true') {
        updateFavicon(newBranding.faviconUrl);
      }
      if (newBranding.pwaIcon) {
        updatePwaIcon(newBranding.pwaIcon);
      }
      
      // Update document title & OG meta tags (only after PIN verified)
      if (sessionStorage.getItem('pin_verified') === 'true') {
        document.title = newBranding.platformName;
      }
      
      return { success: true };
    } catch (error) {
      console.error('Failed to save branding:', error);
      throw error;
    }
  };

  // Change template
  const changeTemplate = (templateId) => {
    const template = designTemplates[templateId];
    if (template) {
      setCurrentTemplate(template);
      setIsDark(template.style === 'dark');
      applyTheme(templateId, branding.customColors, {
        backgroundImage: branding.backgroundImage,
        borderRadius: branding.borderRadius,
      });
    }
  };

  // Toggle dark/light mode (within current template if it supports both)
  const toggleDarkMode = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
    }
  };

  // Preview theme without saving
  const previewTheme = (templateId, customColors = {}, customSettings = {}) => {
    applyTheme(templateId, customColors, customSettings);
  };

  // Reset to saved settings
  const resetPreview = () => {
    applyTheme(branding.templateId, branding.customColors, {
      backgroundImage: branding.backgroundImage,
      borderRadius: branding.borderRadius,
      fontHeading: branding.fontHeading,
      fontBody: branding.fontBody,
    });
  };

  const value = {
    branding,
    currentTemplate,
    templates: designTemplates,
    isDark,
    loading,
    saveBranding,
    uploadMediaFile,
    loadMedia,
    reloadMedia,
    changeTemplate,
    toggleDarkMode,
    previewTheme,
    resetPreview,
    refreshBranding: fetchBranding,
    generateCSSVariables,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
